import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Cluster,
  Container,
  Divider,
  Field,
  Heading,
  Input,
  Modal,
  Select,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Textarea,
} from '../components/ui';

/**
 * The living design system (route: /_ds). Every token + primitive on one page,
 * shareable with the team. It is the source of truth: tune a token or primitive
 * here and every screen that uses it updates - change once, propagate everywhere.
 */

const NAV: { id: string; label: string }[] = [
  { id: 'colour', label: 'Colour' },
  { id: 'type', label: 'Type' },
  { id: 'leading', label: 'Line spacing' },
  { id: 'buttons', label: 'Buttons' },
  { id: 'forms', label: 'Forms' },
  { id: 'spacing', label: 'Spacing' },
  { id: 'composed', label: 'Composed' },
  { id: 'feedback', label: 'Feedback & nav' },
];

function Section({ id, title, kicker, children }: { id?: string; title: string; kicker: string; children: ReactNode }) {
  return (
    // scroll-mt clears the sticky jump-nav when an anchor link lands here.
    <section id={id} className="scroll-mt-20">
      <Stack gap="loose">
        <Stack gap="tight">
          <Text role="eyebrow" faint>{kicker}</Text>
          <Heading level="h2">{title}</Heading>
        </Stack>
        {children}
      </Stack>
    </section>
  );
}

const SWATCHES: Array<{ name: string; cls: string; ring?: boolean }> = [
  { name: 'bg', cls: 'bg-bg', ring: true },
  { name: 'surface', cls: 'bg-surface', ring: true },
  { name: 'bone', cls: 'bg-bone' },
  { name: 'border', cls: 'bg-border' },
  { name: 'text-faint', cls: 'bg-text-faint' },
  { name: 'text-muted', cls: 'bg-text-muted' },
  { name: 'text', cls: 'bg-text' },
  { name: 'ink', cls: 'bg-ink' },
  { name: 'accent', cls: 'bg-accent' },
];

export default function DesignSystem() {
  const [tab, setTab] = useState('live');
  const [seg, setSeg] = useState('email');
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <div data-ds className="min-h-dvh py-10">
      <Container size="md">
        <Stack gap="section">
          {/* Masthead */}
          <Stack gap="tight">
            <Text role="eyebrow" faint>Converted Closet · Design System</Text>
            <Heading level="display">The rulebook, in pixels.</Heading>
            <Text role="lede" muted prose>
              Every building block of the app on one page - colours, type, buttons,
              forms, spacing. This is the source of truth: when we change one of these
              here, every screen that uses it updates automatically. Change once,
              propagate everywhere.
            </Text>
            <Text role="body" muted prose>
              Have a look around and leave us notes on anything - a colour, a size, the
              spacing, the wording. Tip: tap a swatch on the accent bar at the bottom and
              watch everything marked "accent" follow it.
            </Text>
          </Stack>

          {/* Jump nav - sticks to the top so a long page stays navigable. */}
          <nav
            aria-label="Design system sections"
            className="sticky top-0 z-20 border-y border-border bg-bg/95 py-2 backdrop-blur"
          >
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {NAV.map((n) => (
                <a
                  key={n.id}
                  href={`#${n.id}`}
                  className="font-body text-small font-semibold uppercase tracking-[0.04em] text-text-muted hover:text-accent"
                >
                  {n.label}
                </a>
              ))}
            </div>
          </nav>

          {/* Colour */}
          <Section id="colour" kicker="Tokens" title="Colour">
            <Cluster gap="loose">
              {SWATCHES.map((s) => (
                <Stack gap="tight" key={s.name} className="w-[88px]">
                  <div
                    className={`h-16 w-full rounded-md ${s.cls} ${s.ring ? 'border border-border' : ''}`}
                  />
                  <Text role="caption" muted>{s.name}</Text>
                </Stack>
              ))}
            </Cluster>
          </Section>

          {/* Type scale */}
          <Section id="type" kicker="Tokens" title="Type scale">
            <Text role="small" muted prose>
              Trial: one family site-wide - Gotham (Adobe kit). Headers are BOLD
              (700) and CAPS; body is MEDIUM (500). The kit currently has only Book (400)
              + Bold (700), so body shows as Book until a Medium weight is added to the
              kit. Montserrat is the fallback (deleting "gotham" from the tokens
              reverts to it).
            </Text>
            <Stack gap="loose">
              <Heading level="display">Display - Gotham Bold 56/32</Heading>
              <Heading level="h1">Heading 1 - Gotham Bold</Heading>
              <Heading level="h2">Heading 2 - Gotham Bold</Heading>
              <Heading level="h3">Heading 3 - Gotham Bold</Heading>
              <Heading level="h4">Heading 4 - Gotham Book (h3 size, not bold)</Heading>
              <Text role="lede" prose>
                Lede - the intro paragraph voice. Slightly larger than body, same 1.4
                leading, capped measure so it never runs the full width of a wide screen.
              </Text>
              <Text role="body" prose>
                Body - the default reading voice at 16px and 1.4 line-height (Apple's body
                leading). This is the single value shared by lede, body and small, so
                running text now reads identically everywhere instead of drifting between
                1.35 and 1.45. The measure is capped at roughly 68 characters so lines
                stay scannable on desktop.
              </Text>
              <Text role="small" muted>Small - secondary copy, 14px, same 1.4 leading.</Text>
              <Text role="caption" muted>Caption - meta / timestamps, 13px.</Text>
              <Cluster gap="loose">
                <Text role="eyebrow" faint>Eyebrow · kicker</Text>
                <Text role="label" muted>Form label</Text>
              </Cluster>
            </Stack>
          </Section>

          {/* Body leading reference - THE single lever for running-text line spacing */}
          <Section id="leading" kicker="The standard" title="Body line spacing">
            <Text role="small" muted prose>
              Every block of running text in the app uses ONE line-height: 1.4. It is set
              in a single place (--leading-body in tokens.css, mirrored by the --text-*
              roles in ds.css), and every body, lede and small paragraph reads from it -
              so paragraphs are coherent everywhere. The comparison below shows why 1.4:
              1.3 runs a touch tight for long copy, 1.45 read as double-spaced.
            </Text>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
              {([
                { lh: '1.3', note: 'Too tight for long copy', active: false },
                { lh: '1.4', note: 'What we use everywhere', active: true },
                { lh: '1.45', note: 'Read as double-spaced', active: false },
              ] as const).map((col) => (
                <Stack
                  gap="tight"
                  key={col.lh}
                  className={col.active ? 'border-l-2 border-accent pl-5' : 'border-l border-border pl-5'}
                >
                  <Text role="label" faint={!col.active}>
                    {col.lh} · {col.note}{col.active ? ' ✓' : ''}
                  </Text>
                  <p
                    className="font-body text-body text-text max-w-[68ch]"
                    style={{ lineHeight: col.lh }}
                  >
                    She kept the bluebell silk because it carried a whole season in its
                    hem - the market mornings, the slow walk home, the way the light came
                    through it on the line. Some pieces are just cloth. This one was a year.
                  </p>
                </Stack>
              ))}
            </div>
          </Section>

          {/* Buttons */}
          <Section id="buttons" kicker="Primitive" title="Button">
            <Stack gap="loose">
              <Cluster gap="group">
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="soft">Soft</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="accent">Accent</Button>
              </Cluster>
              <Cluster gap="group" align="center">
                <Button size="sm" variant="primary">Small</Button>
                <Button size="md" variant="primary">Medium</Button>
                <Button variant="secondary" disabled>Disabled</Button>
                <Button variant="accent" loading>Loading</Button>
              </Cluster>
              <Cluster gap="group" align="center">
                <Text role="label" faint>Danger tone</Text>
                <Button variant="primary" tone="danger">Delete</Button>
                <Button variant="secondary" tone="danger">Remove</Button>
                <Button variant="soft" tone="danger">Cancel bid</Button>
                <Button variant="ghost" tone="danger">Discard</Button>
              </Cluster>
              <div className="max-w-[320px]">
                <Button variant="accent" fullWidth>Full width</Button>
              </div>
            </Stack>
          </Section>

          {/* Form + Divider */}
          <Section id="forms" kicker="Primitive" title="Form & divider">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Card pad="md">
                <Stack gap="group">
                  <Field label="Email" hint="We'll send a one-time link." required>
                    <Input type="email" placeholder="you@example.com" />
                  </Field>
                  <Field label="Closet" hint="Branded dropdown - matches inputs + buttons.">
                    <Select defaultValue="">
                      <option value="" disabled>Choose a closet</option>
                      <option>Conversion Closeup</option>
                      <option>Saleroom</option>
                      <option>Redesign Challenge</option>
                    </Select>
                  </Field>
                  <Field label="A note" optional>
                    <Textarea placeholder="Optional message" />
                  </Field>
                  <Button variant="primary" fullWidth>Send link</Button>
                  <Divider label="or" />
                  <Button variant="secondary" fullWidth>Use a password instead</Button>
                </Stack>
              </Card>
              <Card pad="md">
                <Stack gap="group">
                  <Text role="label" faint>Error state (aria-invalid + danger)</Text>
                  <Field
                    label="Handle"
                    required
                    error="That handle is already taken."
                  >
                    <Input defaultValue="bluebell" />
                  </Field>
                  <Field
                    label="Bid amount"
                    hint="Minimum is $50."
                    error="Enter at least the minimum bid."
                  >
                    <Input defaultValue="20" inputMode="decimal" />
                  </Field>
                </Stack>
              </Card>
            </div>
          </Section>

          {/* Spacing rhythm */}
          <Section id="spacing" kicker="System" title="Spacing rhythm">
            <Cluster gap="loose" align="start">
              {(['tight', 'snug', 'group', 'loose', 'section'] as const).map((g) => (
                <Card key={g} surface="bone" pad="sm" className="w-[136px]">
                  <Stack gap="tight">
                    <Text role="label" muted>{g}</Text>
                    <Stack gap={g}>
                      <div className="h-3 w-full rounded-sm bg-text-faint/40" />
                      <div className="h-3 w-full rounded-sm bg-text-faint/40" />
                      <div className="h-3 w-full rounded-sm bg-text-faint/40" />
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Cluster>
          </Section>

          {/* Composed sample - proves the primitives cohere */}
          <Section id="composed" kicker="Composed" title="A card, built only from primitives">
            <Card pad="lg" className="max-w-[420px]">
              <Stack gap="group">
                <Stack gap="tight">
                  <Text role="eyebrow" faint>Saleroom</Text>
                  <Heading level="h3">Bluebell, silk midi</Heading>
                </Stack>
                <Stack gap="tight">
                  <Text role="label" muted>Current bid</Text>
                  <Heading level="h2" as="div">$240.00</Heading>
                  <Text role="caption" muted>12 bids · 38 watching</Text>
                </Stack>
                <Stack gap="tight">
                  <Button variant="accent" fullWidth>Place bid</Button>
                  <Button variant="secondary" fullWidth>Buy now · $600</Button>
                </Stack>
              </Stack>
            </Card>
          </Section>

          {/* Feedback + navigation primitives */}
          <Section id="feedback" kicker="Primitive" title="Badge, Banner, Tabs, Skeleton, Modal">
            <Stack gap="loose">
              <Cluster gap="group" align="center">
                <Badge>Neutral</Badge>
                <Badge tone="ink" solid>LOT KH-002</Badge>
                <Badge tone="accent" solid>Live now</Badge>
                <Badge tone="success">Available</Badge>
                <Badge tone="warning">Closing soon</Badge>
                <Badge tone="danger">Outbid</Badge>
                <Badge tone="neutral">12 bids</Badge>
              </Cluster>

              <Stack gap="tight" className="max-w-[520px]">
                <Banner tone="info" title="Heads up">A neutral informational notice.</Banner>
                <Banner tone="success" title="Saved">Your changes were published.</Banner>
                <Banner tone="warning" title="Card expiring">Update your card before the next charge.</Banner>
                <Banner
                  tone="danger"
                  title="Encode failed"
                  action={<Button size="sm" variant="secondary" tone="danger">Retry</Button>}
                  onClose={() => {}}
                >
                  Two videos failed to process.
                </Banner>
              </Stack>

              <Cluster gap="section" align="start">
                <Tabs
                  aria-label="Underline tabs"
                  items={[
                    { value: 'live', label: 'Live now' },
                    { value: 'soon', label: 'Coming soon' },
                    { value: 'archive', label: 'Archive' },
                  ]}
                  value={tab}
                  onChange={setTab}
                />
                <Tabs
                  variant="segmented"
                  aria-label="Segmented control"
                  items={[
                    { value: 'email', label: 'Email link' },
                    { value: 'password', label: 'Password' },
                  ]}
                  value={seg}
                  onChange={setSeg}
                />
              </Cluster>

              <Stack gap="tight" className="max-w-[280px]">
                <Skeleton className="aspect-[4/5] w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </Stack>

              <div>
                <Button variant="secondary" onClick={() => setModalOpen(true)}>Open modal</Button>
                <Modal
                  open={modalOpen}
                  onClose={() => setModalOpen(false)}
                  title="Confirm"
                  footer={
                    <>
                      <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
                      <Button variant="accent" onClick={() => setModalOpen(false)}>Confirm</Button>
                    </>
                  }
                >
                  <Text role="body">
                    A plain content/confirm dialog: portalled, scroll-locked, Esc and
                    backdrop close, focus trapped, bottom sheet on mobile.
                  </Text>
                </Modal>
              </div>
            </Stack>
          </Section>
        </Stack>
      </Container>
    </div>
  );
}
